import { graphql } from 'react-relay';

import type { UserPreferences, UserPreferencesProvider } from '@atlaskit/editor-common/types';

import { getApolloClient } from '@confluence/graphql/entry-points/getApolloClient.ts';
import { confluenceLocalStorageInstance } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { persistBannerDismissalNewPinning } from '@confluence/editor-controls-banner-dismissal';
import { readQueryInterop } from '@confluence/apollo-relay-interop/entry-points/readQuery.ts';
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';
import { mutateInterop } from '@confluence/apollo-relay-interop/entry-points/mutate.ts';
import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';

// eslint-disable-next-line import/no-cycle
import { parseConfluenceEditorSettings } from './EditorPreferences.ts';
import type {
	EditorUserPreferencesProviderQuery$data,
	EditorUserPreferencesProviderQuery$variables,
} from './__generated__/EditorUserPreferencesProviderQuery.graphql.ts';
import type {
	EditorUserPreferencesProviderMutation$data,
	EditorUserPreferencesProviderMutation$variables,
} from './__generated__/EditorUserPreferencesProviderMutation.graphql.ts';

const EditorUserPreferencesProviderQuery = graphql`
	query EditorUserPreferencesProviderQuery {
		userPreferences {
			confluenceEditorSettings {
				toolbarDockingInitialPosition
			}
		}
	}
`;

const EditorUserPreferencesProviderMutation = graphql`
	mutation EditorUserPreferencesProviderMutation($toolbarDockingInitialPosition: String!) {
		updateUserPreferences(
			userPreferences: {
				confluenceEditorSettingsInput: {
					toolbarDockingInitialPosition: $toolbarDockingInitialPosition
				}
			}
		) {
			confluenceEditorSettings {
				toolbarDockingInitialPosition
			}
		}
	}
`;

enum Status {
	Complete = 'complete',
	Error = 'error',
	Pending = 'pending',
}

// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
export const isSSR = (): boolean => Boolean(process.env.REACT_SSR);

type EditorUserPreferencesProviderOptions = {
	getFallbackUserPreferences?: () => UserPreferences;
};

export class EditorUserPreferencesProvider implements UserPreferencesProvider {
	private userPreferences: UserPreferences = {};
	private getFallbackUserPreferences: (() => UserPreferences) | undefined;
	// We use getApolloClient to get the apollo client instance
	// This is to avoid adding required apollo context
	private apolloClient = getApolloClient();
	private providerStatus: Status = Status.Pending;

	public constructor(options: EditorUserPreferencesProviderOptions = {}) {
		this.getFallbackUserPreferences = options.getFallbackUserPreferences;
		this.loadPreferencesFromCache();

		this.loadPreferencesInternal()
			.then(() => {
				this.providerStatus = Status.Complete;
			})
			.catch(() => {
				this.providerStatus = Status.Error;
			});
	}

	// synchronously load preferences from cache
	private loadPreferencesFromCache(): UserPreferences | undefined {
		if (!isSSR()) {
			const userPreferenceStorage =
				confluenceLocalStorageInstance &&
				confluenceLocalStorageInstance.getItem(keys.EDITOR_CONTROLS_INITIAL_PREFERENCES);
			if (userPreferenceStorage) {
				this.userPreferences = JSON.parse(userPreferenceStorage);
			}
		}

		if (!this.apolloClient) {
			return undefined;
		}

		try {
			const result = readQueryInterop<
				EditorUserPreferencesProviderQuery$data,
				EditorUserPreferencesProviderQuery$variables
			>({
				query: EditorUserPreferencesProviderQuery,
			});

			if (result?.userPreferences) {
				this.userPreferences = parseConfluenceEditorSettings(
					result.userPreferences?.confluenceEditorSettings,
				);

				// if we have a cached value, we can set the status to complete
				// since we are not going to make a network request
				// to fetch the preferences with loadPreferencesInternal and not skipCache
				if (this.providerStatus !== Status.Complete) {
					this.providerStatus = Status.Complete;
				}
			}

			return this.userPreferences;
		} catch {
			return this.userPreferences;
		}
	}

	private async loadPreferencesInternal(options?: {
		skipCache?: boolean;
	}): Promise<UserPreferences> {
		if (!this.apolloClient) {
			return Promise.reject('No apollo client');
		}

		const { data } = await queryInterop<
			EditorUserPreferencesProviderQuery$data,
			EditorUserPreferencesProviderQuery$variables
		>({
			query: EditorUserPreferencesProviderQuery,
			fetchPolicy: options?.skipCache ? 'no-cache' : 'cache-first',
		});

		this.userPreferences = parseConfluenceEditorSettings(
			data?.userPreferences?.confluenceEditorSettings,
		);

		this.updateLocalStorage();

		return this.userPreferences;
	}

	async loadPreferences(): Promise<UserPreferences> {
		try {
			const userPreferences = await this.loadPreferencesInternal({ skipCache: true });
			// can recover from the status previous error
			if (this.providerStatus !== Status.Complete) {
				this.providerStatus = Status.Complete;
			}
			return userPreferences;
		} catch (error) {
			// We will ignore subssequent errors if thte status was complete
			// And will use the data from previous request
			if (this.providerStatus !== Status.Complete) {
				this.providerStatus = Status.Error;
			}
			return Promise.reject(error);
		}
	}

	private updateLocalStorage(): void {
		if (!isSSR()) {
			confluenceLocalStorageInstance.setItem(
				keys.EDITOR_CONTROLS_INITIAL_PREFERENCES,
				JSON.stringify(this.userPreferences),
			);
		}
	}

	async updatePreference<K extends keyof UserPreferences>(
		key: K,
		value: UserPreferences[K],
	): Promise<void> {
		if (!this.apolloClient) {
			return Promise.reject('No apollo client');
		}

		const variables = {
			[key as keyof EditorUserPreferencesProviderMutation$variables]: value,
		} as EditorUserPreferencesProviderMutation$variables;

		this.userPreferences[key] = value;

		const { data } = await mutateInterop<
			EditorUserPreferencesProviderMutation$data,
			EditorUserPreferencesProviderMutation$variables
		>({
			mutation: EditorUserPreferencesProviderMutation,
			variables,
			update: () => {
				// this is required to indicate toolbar changes to any useQuery for EditorPreferencesQuery, particularly progressive object header
				const getValue = () => value ?? null;

				writeQueryInterop({
					query: EditorUserPreferencesProviderQuery,
					data: {
						userPreferences: {
							__typename: 'UserPreferences',
							confluenceEditorSettings: {
								__typename: 'ConfluenceEditorSettings',
								toolbarDockingInitialPosition: getValue(),
							},
						},
					},
				});
			},
		});

		const updatedUserPreferences = parseConfluenceEditorSettings(
			data?.updateUserPreferences?.confluenceEditorSettings,
		);

		this.userPreferences = {
			...this.userPreferences,
			...updatedUserPreferences,
		};

		if (value === 'top') {
			persistBannerDismissalNewPinning('toolbar-inline');
		} else if (value === 'none') {
			persistBannerDismissalNewPinning('toolbar-top');
		}
		this.updateLocalStorage();
	}

	getPreference<K extends keyof UserPreferences>(key: K): UserPreferences[K] | undefined | null {
		const fallbackUserPreferences = this.getFallbackUserPreferences?.() || {};
		return this.userPreferences[key] || fallbackUserPreferences[key];
	}
}
